import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Plus, Trash2, CreditCard, Edit, Banknote, CalendarDays, CheckCircle2 } from 'lucide-react';
import PaymentMethodModal from './PaymentMethodModal';
import { parseMoney } from '../../utils/format';
import { PaymentMethod } from '../../services/saleService';


interface Step3PaymentConditionsProps {
    totalAmount: number;
    baseCurrency: 'ARS' | 'USD';
    paymentMethods: PaymentMethod[];
    onPaymentMethodsChange: (methods: PaymentMethod[]) => void;
    exchangeRate: string;
    onExchangeRateChange: (rate: string) => void;
}

const Step3PaymentConditions: React.FC<Step3PaymentConditionsProps> = ({
    totalAmount,
    baseCurrency,
    paymentMethods,
    onPaymentMethodsChange,
    exchangeRate,
    onExchangeRateChange
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);

    const handleAddPayment = (payment: PaymentMethod) => {
        onPaymentMethodsChange([...paymentMethods, payment]);
        setEditingMethod(null);
    };

    const handleUpdatePayment = (updatedPayment: PaymentMethod) => {
        onPaymentMethodsChange(
            paymentMethods.map(method =>
                method.id === updatedPayment.id ? updatedPayment : method
            )
        );
        setEditingMethod(null);
    };

    const handleEditPayment = (method: PaymentMethod) => {
        setEditingMethod(method);
        setIsModalOpen(true);
    };

    const handleRemovePayment = (id: string) => {
        if (!window.confirm('¿Eliminar este método de pago?')) return;
        onPaymentMethodsChange(paymentMethods.filter(p => p.id !== id));
    };

    const convertToBaseCurrency = (amount: number, currency: 'ARS' | 'USD', methodExchangeRate: string): number => {
        const rate = parseFloat(methodExchangeRate) || 1;
        if (currency === baseCurrency) return amount;
        if (baseCurrency === 'USD') return amount / rate;
        return amount * rate;
    };

    const currentAmount = paymentMethods.reduce((sum, method) => {
        const rawAmount = parseMoney(method.amount) || 0;
        const convertedAmount = convertToBaseCurrency(rawAmount, method.currency, method.exchangeRate);
        return sum + convertedAmount;
    }, 0);

    const remainingAmount = totalAmount - currentAmount;
    const progressPercentage = totalAmount > 0 ? (currentAmount / totalAmount) * 100 : 0;
    const isComplete = progressPercentage >= 99.99;

    const formatDisplayDate = (dateString?: string) => {
        if (!dateString) return '—';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getPaymentTypeLabel = (type: 'cash' | 'financing') => {
        return type === 'cash' ? 'Efectivo' : 'Financiado';
    };

    const getFinancingTypeLabel = (type?: string) => {
        const labels: { [key: string]: string } = {
            cuotas_fijas: 'Cuotas Fijas',
            cuotas_variables: 'Cuotas Variables',
            plan_especial: 'Plan Especial'
        };
        return type ? labels[type] || type : '—';
    };

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-6 lg:p-8 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-primary" />
                            Condiciones de Pago
                        </h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            Configure los métodos de pago para cubrir el total de la venta
                        </p>
                    </div>
                    <Button
                        onClick={() => setIsModalOpen(true)}
                        disabled={isComplete}
                        size="sm"
                        className="gap-2 shrink-0"
                    >
                        <Plus className="w-4 h-4" />
                        Agregar Método
                    </Button>
                </div>

                {/* Progress Summary */}
                <div className="rounded-xl border border-border overflow-hidden">
                    {/* Progress bar header */}
                    <div className="px-5 pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-foreground">Cobertura del pago</p>
                            <div className="flex items-center gap-2">
                                {isComplete && (
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                )}
                                <span className={`text-sm font-bold tabular-nums ${isComplete ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}>
                                    {progressPercentage.toFixed(1)}%
                                </span>
                            </div>
                        </div>
                        <div className="w-full bg-border/60 rounded-full h-2 overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                    isComplete ? 'bg-emerald-500' : 'bg-primary'
                                }`}
                                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                            />
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 divide-x divide-border border-t border-border">
                        <div className="px-5 py-4">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Total Venta</p>
                            <p className="text-base font-bold text-foreground tabular-nums">
                                {baseCurrency} {totalAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div className="px-5 py-4">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Cubierto</p>
                            <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                                {baseCurrency} {currentAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div className="px-5 py-4">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Restante</p>
                            <p className={`text-base font-bold tabular-nums ${
                                remainingAmount <= 0.01
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : 'text-amber-600 dark:text-amber-400'
                            }`}>
                                {baseCurrency} {Math.max(0, remainingAmount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Payment Methods List */}
                {paymentMethods.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-14 text-center border-2 border-dashed border-border rounded-xl">
                        <div className="w-14 h-14 rounded-2xl bg-muted/60 flex items-center justify-center mb-4">
                            <CreditCard className="w-7 h-7 text-muted-foreground/60" />
                        </div>
                        <p className="text-sm font-medium text-foreground mb-1">Sin métodos de pago</p>
                        <p className="text-xs text-muted-foreground max-w-xs mb-5">
                            Agregue al menos un método que cubra el total de la venta
                        </p>
                        <Button onClick={() => setIsModalOpen(true)} variant="outline" size="sm" className="gap-2">
                            <Plus className="w-4 h-4" />
                            Agregar primer método
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {paymentMethods.map((method, index) => {
                            const isCash = method.paymentType === 'cash';
                            return (
                                <div
                                    key={method.id}
                                    className="rounded-xl border border-border bg-card overflow-hidden transition-shadow hover:shadow-sm"
                                >
                                    {/* Method Header */}
                                    <div className="flex items-center gap-3 px-4 py-3 border-b border-border/60 bg-muted/20">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                            isCash
                                                ? 'bg-emerald-100 dark:bg-emerald-900/30'
                                                : 'bg-violet-100 dark:bg-violet-900/30'
                                        }`}>
                                            {isCash ? (
                                                <Banknote className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                            ) : (
                                                <CalendarDays className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-foreground truncate">
                                                {method.description || `Método de Pago ${index + 1}`}
                                            </p>
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                                <Badge
                                                    variant="outline"
                                                    className={`text-[10px] px-1.5 py-0 font-medium ${
                                                        isCash
                                                            ? 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400'
                                                            : 'border-violet-300 text-violet-700 dark:border-violet-800 dark:text-violet-400'
                                                    }`}
                                                >
                                                    {getPaymentTypeLabel(method.paymentType)}
                                                </Badge>
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                    {method.currency}
                                                </Badge>
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                    Tipo {method.accountingType}
                                                </Badge>
                                            </div>
                                        </div>

                                        {/* Amount */}
                                        <div className="text-right shrink-0 mr-2">
                                            <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                                                {method.currency} {method.amount}
                                            </p>
                                            {method.currency !== baseCurrency && (() => {
                                                const rawAmount = parseMoney(method.amount) || 0;
                                                const converted = convertToBaseCurrency(rawAmount, method.currency, method.exchangeRate);
                                                return (
                                                    <p className="text-xs text-muted-foreground tabular-nums">
                                                        ≈ {baseCurrency} {converted.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                                    </p>
                                                );
                                            })()}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => handleEditPayment(method)}
                                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                title="Editar método"
                                            >
                                                <Edit className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleRemovePayment(method.id)}
                                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                                title="Eliminar método"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Method Details */}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 px-4 py-3">
                                        {/* IVA */}
                                        {method.accountingType === 'A' && (
                                            <div>
                                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">IVA</p>
                                                <p className="text-sm font-medium text-foreground">{method.iva}%</p>
                                            </div>
                                        )}

                                        {/* Cash: Fecha de Pago */}
                                        {isCash && (
                                            <div>
                                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Fecha de Pago</p>
                                                <p className="text-sm font-medium text-foreground">{formatDisplayDate(method.paymentDate)}</p>
                                            </div>
                                        )}

                                        {/* Financing Details */}
                                        {!isCash && (
                                            <>
                                                <div>
                                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Financiación</p>
                                                    <p className="text-sm font-medium text-foreground">{getFinancingTypeLabel(method.financingType)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Cuotas</p>
                                                    <p className="text-sm font-medium text-foreground">{method.installments || '—'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">1ra Cuota</p>
                                                    <p className="text-sm font-medium text-foreground">{formatDisplayDate(method.firstInstallmentDate)}</p>
                                                </div>
                                                {method.currency === 'ARS' && method.updateIndex && (
                                                    <div>
                                                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Índice</p>
                                                        <p className="text-sm font-medium text-foreground">{method.updateIndex}</p>
                                                    </div>
                                                )}
                                                {method.currency === 'ARS' && method.indexBaseDate && (
                                                    <div>
                                                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Fecha Base</p>
                                                        <p className="text-sm font-medium text-foreground">{formatDisplayDate(method.indexBaseDate)}</p>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Exchange Rate */}
                                        {method.currency !== baseCurrency && (
                                            <div>
                                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Cotización</p>
                                                <p className="text-sm font-medium text-foreground">1 USD = {method.exchangeRate} ARS</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Payment Method Modal */}
            <PaymentMethodModal
                open={isModalOpen}
                onOpenChange={(open) => {
                    setIsModalOpen(open);
                    if (!open) setEditingMethod(null);
                }}
                onAddPayment={handleAddPayment}
                onUpdatePayment={handleUpdatePayment}
                editingMethod={editingMethod}
                totalAmount={totalAmount}
                baseCurrency={baseCurrency}
                currentAmount={currentAmount}
                exchangeRate={exchangeRate}
                onExchangeRateChange={onExchangeRateChange}
            />
        </div>
    );
};

export default Step3PaymentConditions;
